import React from 'react';
import { BlankModal, HStack, ModalBody, ModalFooter, ModalHeader } from '@cad-arc';
import { useForm } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import { useApiClient } from '@m43/core';
import { FormBody } from '../forms/integrated-form-components/form-body';
import { FormCancelButton } from '../forms/integrated-form-components/form-cancel-button';
import { FormSaveButton } from '../forms/integrated-form-components/form-save-button';
import { useFullscreenModalControls } from '../modals/context/modal-context-provider';
import { FormInput } from '../forms/integrated-form-controls/form-input';
import { WorkspaceTemplateType } from './types';
import { errorOccurredWhile } from '../common/helpers/error-occurred-while';
import { IntegratedFormProvider } from '../forms/integrated-form-provider';
import { WorkspacesResource } from '@mark43/cad-api';

type SaveWorkspaceAsForm = {
    templateName: string;
};

export type SaveWorkspaceAsModalComponentType = typeof SaveWorkspaceAsModal;
export function SaveWorkspaceAsModal(props: {
    workspaceTemplateType: WorkspaceTemplateType;
}): React.ReactElement {
    const modalControls = useFullscreenModalControls();
    const onCancel = React.useCallback(() => {
        modalControls.close();
    }, [modalControls]);
    const apiClient = useApiClient();
    const form = useForm<SaveWorkspaceAsForm>({
        defaultValues: { templateName: '' },
        mode: 'onBlur',
    });
    const mutation = useMutation(
        (args: SaveWorkspaceAsForm) => {
            return props.workspaceTemplateType === 'USER'
                ? apiClient.request<WorkspacesResource.CreateUserWorkspaceTemplate>({
                      method: 'POST',
                      url: 'workspaces/user/template',
                      data: args,
                  })
                : apiClient.request<WorkspacesResource.CreateDepartmentWorkspaceTemplate>({
                      method: 'POST',
                      url: 'workspaces/department/template',
                      data: args,
                  });
        },
        {
            onSuccess() {
                onCancel();
            },
        }
    );
    return (
        <IntegratedFormProvider mutation={mutation} form={form}>
            <BlankModal size="md" onClose={onCancel} isOpen={true}>
                <ModalHeader>
                    {props.workspaceTemplateType === 'USER'
                        ? 'Save Workspace As'
                        : 'Save Department Template As'}
                </ModalHeader>
                <ModalBody>
                    <FormBody
                        saveErrorMessage={errorOccurredWhile(
                            props.workspaceTemplateType === 'USER'
                                ? 'saving workspace'
                                : 'saving department template'
                        )}
                    >
                        <FormInput
                            autoFocus={true}
                            label="Workspace Name"
                            name="templateName"
                            control={form.control}
                            rules={{ required: 'Required' }}
                        />
                    </FormBody>
                </ModalBody>
                <ModalFooter>
                    <HStack>
                        <FormCancelButton onClick={onCancel} />
                        <FormSaveButton />
                    </HStack>
                </ModalFooter>
            </BlankModal>
        </IntegratedFormProvider>
    );
}
